import {Dropdown, IconButton, useTranslationContext} from 'platform/components';
import {Show} from 'platform/foundation';

import {useState} from 'react';

import type {EagColumn} from '../../../../../../types/EagColumn';
import type {EagColumnState} from '../../../../../../types/EagColumnState';
import {MobileSheet} from '../../../../../MobileSheet/MobileSheet';
import {getColumnMenuMaxHeight} from '../../utils/getColumnMenuMaxHeight';
import {ColumnHeaderMenu} from '../ColumnHeaderMenu/ColumnHeaderMenu';

type ColumnMenuDropdownProps = {
  isOpen: boolean;
  setIsMenuOpen: (open: boolean) => void;
  column: EagColumn;
  columnPinned: EagColumnState['pinned'];
  useSmallButtons: boolean;
  menuPriority?: 'primary';
  /** floating-ui boundary — the grid container, keeps the menu horizontally inside the grid. */
  menuBoundary?: HTMLElement | null;
};

/**
 * Mobile counterpart to ColumnMenuDropdown — same trigger + same ColumnHeaderMenu
 * content, but the container is a MobileSheet instead of <Dropdown>. No floating-ui
 * Dropdown is mounted here at all, so its click-outside/positioning logic never runs
 * on mobile (the sheet's own scrim handles dismiss instead), and there is no risk of
 * the trigger tap being seen as an outside click that immediately re-closes the sheet.
 */
function ColumnMenuMobileSheet(props: ColumnMenuDropdownProps) {
  const t = useTranslationContext();

  const handleTriggerClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    props.setIsMenuOpen(true);
  };

  const handleClose = () => {
    props.setIsMenuOpen(false);
  };

  return (
    <>
      <IconButton
        icon="navigation/menu"
        size={props.useSmallButtons ? 'small' : 'default'}
        priority={props.menuPriority}
        aria-label={t('page.dataGrid.column.optionsAriaLabel', {
          columnName: props.column.headerName,
        })}
        onClick={handleTriggerClick}
      />
      <MobileSheet
        isOpen={props.isOpen}
        onClose={handleClose}
        title={props.column.headerName}
        data-testid="columnHeaderMenu"
      >
        <ColumnHeaderMenu
          column={props.column}
          columnPinned={props.columnPinned}
          isOpen={props.isOpen}
          onClose={handleClose}
          isMobile
        />
      </MobileSheet>
    </>
  );
}

function ColumnMenuDropdown(props: ColumnMenuDropdownProps) {
  const t = useTranslationContext();

  // Measured on every trigger click (keyboard activation of a button also fires
  // click) — the menu opens below the trigger, so the space between the trigger's
  // bottom edge and the viewport bottom is what the menu may occupy (T20-136597).
  const [menuMaxHeight, setMenuMaxHeight] = useState<number | undefined>(undefined);

  const handleMenuButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setMenuMaxHeight(getColumnMenuMaxHeight(event.currentTarget.getBoundingClientRect().bottom));
  };

  return (
    <Dropdown
      isOpen={props.isOpen}
      onOpen={() => props.setIsMenuOpen(true)}
      onClose={() => props.setIsMenuOpen(false)}
      placement="bottom-end"
      boundary={props.menuBoundary ?? undefined}
      onButtonClick={handleMenuButtonClick}
      isFocusManagementDisabled
      isKeyboardNavigationDisabled
      dropdownControl={
        <IconButton
          icon="navigation/menu"
          size={props.useSmallButtons || props.menuPriority === 'primary' ? 'small' : 'default'}
          priority={props.menuPriority}
          aria-label={t('page.dataGrid.column.optionsAriaLabel', {
            columnName: props.column.headerName,
          })}
        />
      }
    >
      <ColumnHeaderMenu
        column={props.column}
        columnPinned={props.columnPinned}
        isOpen={props.isOpen}
        onClose={() => props.setIsMenuOpen(false)}
        maxHeight={menuMaxHeight}
      />
    </Dropdown>
  );
}

type HeaderCellActionsProps = {
  column: EagColumn;
  columnPinned: EagColumnState['pinned'];
  isMobile: boolean;
  useSmallButtons: boolean;
  showMenuButton: boolean;
  showColumnMenu: boolean;
  columnHasMenuContent: boolean;
  menuPriority?: 'primary';
  isMenuOpen: boolean;
  setIsMenuOpen: (open: boolean) => void;
  /** Always-visible sort indicator, rendered LAST in the cluster (far right of the cell). */
  sortIndicator?: React.ReactNode;
  /** floating-ui boundary — the grid container, keeps the menu horizontally inside the grid. */
  menuBoundary?: HTMLElement | null;
};

/**
 * The header cell's action-button cluster: absolute, right-aligned, vertically centered,
 * sitting OUTSIDE the drag-listener zone. Order (left → right): menu trigger, sort
 * indicator — the sort indicator is always the rightmost element. On mobile the menu
 * trigger is an always-visible hamburger; on desktop it is hover-revealed. All state
 * and handlers live in HeaderCell.
 */
export function HeaderCellActions(props: HeaderCellActionsProps) {
  const showMobileMenu = props.isMobile && props.showColumnMenu && props.columnHasMenuContent;
  const showDesktopMenu = !props.isMobile && props.showMenuButton && props.showColumnMenu;

  if (!showMobileMenu && !showDesktopMenu && props.sortIndicator == null) {
    return null;
  }

  // Hover-revealed: hidden until the header cell (group/header on the <th>) is
  // hovered or the button receives focus. Kept permanently visible while the menu
  // is open (pointer moves into the dropdown) and when the column has an active
  // filter (the blue button doubles as the filter indicator).
  const isMenuButtonPersistent = props.isMenuOpen || props.menuPriority === 'primary';

  return (
    <div className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-1 pr-1">
      <Show when={showMobileMenu}>
        <ColumnMenuMobileSheet
          isOpen={props.isMenuOpen}
          setIsMenuOpen={props.setIsMenuOpen}
          column={props.column}
          columnPinned={props.columnPinned}
          useSmallButtons={props.useSmallButtons}
        />
      </Show>
      <Show when={showDesktopMenu}>
        <div
          className={`bg-[#FAFBFC] ${
            props.menuPriority === 'primary' ? '[&_button]:!size-5 [&_svg]:!size-3' : ''
          } ${
            isMenuButtonPersistent
              ? ''
              : 'opacity-0 group-hover/header:opacity-100 focus-within:opacity-100'
          }`}
        >
          <ColumnMenuDropdown
            isOpen={props.isMenuOpen}
            setIsMenuOpen={props.setIsMenuOpen}
            column={props.column}
            columnPinned={props.columnPinned}
            useSmallButtons={props.useSmallButtons}
            menuPriority={props.menuPriority}
            menuBoundary={props.menuBoundary}
          />
        </div>
      </Show>
      {props.sortIndicator}
    </div>
  );
}
